import { appSelectors } from '@appStore/reducers/app'
import type { Profile } from 'api/connector/types'
import { connectorApi } from 'api/connector/api.ts'
import unLinked from 'assets/image/auth-steps/unLinked.png'
import clsx from 'clsx'
import { AuthTemplate } from 'components/templates/authTemplate'
import {
	LinkedInLoginPhase,
	mapLoginProfileToConnectorProfile,
	useLinkedInPasswordLogin
} from 'features/linkedin-login'
import { LinkedInLoginV2 } from 'features/linkedin-login/LinkedInLoginV2'
import type { LinkedInLoginHeaders } from 'features/linkedin-login/types'
import { useEffect, useState } from 'react'
import { notifyError } from 'shared/errors/notificate.ts'
import { useAppSelector, useSwitcher } from 'shared/hooks/index'
import { typo } from 'shared/styles'
import { LoadingButton } from 'shared/ui'

import css from './NoLinkedIn.module.scss'
import { ModalConfirm } from './ModalConfirm.tsx'

const cn = AuthTemplate.cn

export function NoLinkedIn() {
	const email = useAppSelector(
		state => appSelectors.appProfile(state)?.email ?? ''
	)
	const login = useLinkedInPasswordLogin({
		skipWorkspaceAutomationModeCheck: true
	})
	const modalSwitch = useSwitcher(false)
	const [confirmProfile, setConfirmProfile] = useState<Profile | null>(null)
	const [confirmHeaders, setConfirmHeaders] =
		useState<LinkedInLoginHeaders | null>(null)
	const [correlationId, setCorrelationId] = useState<string | null>(null)

	const [signOut, { isLoading: isSigningOut }] =
		connectorApi.useSignOutMutation()

	const logout = () => {
		if (!isSigningOut) {
			signOut().unwrap().catch(notifyError)
		}
	}

	const handleConfirmClose = () => {
		modalSwitch.off()
		setConfirmProfile(null)
		setConfirmHeaders(null)
		setCorrelationId(null)
		login.close()
	}

	useEffect(() => {
		if (login.phase !== LinkedInLoginPhase.Done) {
			return
		}

		const mapped = mapLoginProfileToConnectorProfile(login.result?.profile)
		if (!mapped) {
			notifyError(
				new Error(
					'LinkedIn login succeeded, but profile was not returned. Try again.'
				)
			)
			login.close()
			return
		}

		const headers = login.result?.headers ?? null
		if (!headers?.cookies?.length) {
			notifyError(
				new Error(
					'LinkedIn login succeeded, but session was not returned. Try again.'
				)
			)
			login.close()
			return
		}

		setConfirmProfile(mapped)
		setConfirmHeaders(headers)
		modalSwitch.on()
	}, [login.phase, login.result])

	return (
		<AuthTemplate active={3} className={css.root}>
			<span className={clsx(css.email, typo.mediumBody)}>{email}</span>

			<h1 className={clsx(cn.title, css.title)}>Link your LinkedIn account</h1>

			{login.phase === LinkedInLoginPhase.Done && (
				<img alt='unLinked' className={cn.image} src={unLinked} width={130} />
			)}
			<p className={clsx(cn.text, css.text)}>
				Sign in with your LinkedIn account.
			</p>

			{login.phase === LinkedInLoginPhase.Error && login.error && (
				<p className={css.error}>{login.error}</p>
			)}

			{login.phase !== LinkedInLoginPhase.Done && (
				<div className={css.browser}>
					<LinkedInLoginV2
						onFailed={message => login.setError(message)}
						onSolved={solved => {
							setCorrelationId(solved.correlationId ?? null)
							void login.finishCaptcha(
								solved.headers,
								solved.profile,
								solved.correlationId
							)
						}}
					/>
				</div>
			)}

			<div className={css.logout}>
				<LoadingButton
					className={cn.action}
					isLoading={isSigningOut}
					onClick={logout}
					type='button'
					typography={typo.largeButton}
					variant='secondary'
				>
					Log Out
				</LoadingButton>
			</div>

			<ModalConfirm
				correlationId={correlationId}
				headers={confirmHeaders}
				onClose={handleConfirmClose}
				open={modalSwitch.isOn}
				profile={confirmProfile ?? undefined}
			/>
		</AuthTemplate>
	)
}
